// B7 批次 user 端 hooks(2026-05-21)
//
// 3 子系统:
//   - wallet_payout_account 绑定(SlxwM USDT / i2QnN 银行卡)
//   - oauth_account 关联(GmWWr 登录 / zPcq8 管理)
//   - 注销账户(b9rrI2)
//
// 后端 endpoint 详见 server/internal/httpapi/b7_handlers.go

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@dogdisk/shared'

// ──────────────────── Payout accounts ────────────────────

export type PayoutAccountRow = {
  id: string
  user_id: string
  type: 'usdt' | 'bank'
  address?: string
  bank_name?: string
  account_no?: string
  account_holder?: string
  status: 'active' | 'deleted'
  verified_at?: string
  created_at: string
  updated_at: string
}

export function useMyPayoutAccounts() {
  return useQuery({
    queryKey: ['user', 'payout_accounts'] as const,
    queryFn: () => apiClient.get<{ accounts: PayoutAccountRow[] }>('/gapi/user/payout-accounts'),
    staleTime: 30_000,
  })
}

export type BindPayoutInput =
  | { type: 'usdt'; address: string }
  | { type: 'bank'; bank_name: string; account_no: string; account_holder: string }

export function useBindPayoutAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: BindPayoutInput) =>
      apiClient.post<PayoutAccountRow>('/gapi/user/payout-accounts', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user', 'payout_accounts'] }),
  })
}

export function useRemovePayoutAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string }) =>
      apiClient.del<{ ok: true }>(`/gapi/user/payout-accounts/${input.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user', 'payout_accounts'] }),
  })
}

// ──────────────────── OAuth accounts ────────────────────

export type OAuthAccountRow = {
  id: string
  user_id: string
  provider: 'google' | 'github'
  subject: string
  created_at: string
}

export function useMyOAuthAccounts() {
  return useQuery({
    queryKey: ['user', 'oauth_accounts'] as const,
    queryFn: () => apiClient.get<{ accounts: OAuthAccountRow[] }>('/gapi/user/oauth-accounts'),
    staleTime: 30_000,
  })
}

export function useUnbindOAuthAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: string }) =>
      apiClient.del<{ ok: true }>(`/gapi/user/oauth-accounts/${input.id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user', 'oauth_accounts'] }),
  })
}

// ──────────────────── Deactivate account ────────────────────

export type DeactivationStatus = {
  status: 'active' | 'pending_deletion'
  requested_at?: string
  expires_at?: string
}

export function useDeactivationStatus() {
  return useQuery({
    queryKey: ['user', 'deactivation_status'] as const,
    queryFn: () => apiClient.get<DeactivationStatus>('/gapi/user/account/deactivation'),
    staleTime: 0,
  })
}

export function useRequestDeactivation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => apiClient.post<DeactivationStatus>('/gapi/user/account/deactivation', {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user', 'deactivation_status'] }),
  })
}

export function useCancelDeactivation() {
  const qc = useQueryClient()
  return useMutation({
    // P3-W8(2026-05-25):改走独立 endpoint(与 deactivate-account?cancel=true 兼容并存)
    mutationFn: () =>
      apiClient.del<DeactivationStatus>('/gapi/user/account/deactivation'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['user', 'deactivation_status'] }),
  })
}
